import {
  collection,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  serverTimestamp,
  runTransaction,
  Timestamp
} from 'firebase/firestore';
import { db } from './config';

const COLLECTION = 'invoices';
const SETTINGS_DOC = 'settings/website';

// Generate unique ID for line items
const generateId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Helper to calculate invoice totals
const calculateTotals = (lineItems, discount = null, taxRate = 10) => {
  const subtotal = lineItems.reduce((sum, item) => sum + (item.total || 0), 0);

  let discountAmount = 0;
  if (discount) {
    if (discount.type === 'percentage') {
      discountAmount = (subtotal * discount.value) / 100;
    } else {
      discountAmount = discount.value || 0;
    }
  }

  const taxableAmount = subtotal - discountAmount;
  const taxAmount = (taxableAmount * taxRate) / 100;
  const total = taxableAmount + taxAmount;

  return {
    subtotal,
    discountAmount,
    taxAmount,
    total
  };
};

// Calculate due date based on payment terms
const calculateDueDate = (paymentTerms) => {
  const now = new Date();
  let daysToAdd = 14; // default

  switch (paymentTerms) {
    case 'due_on_receipt':
      daysToAdd = 0;
      break;
    case 'net_7':
      daysToAdd = 7;
      break;
    case 'net_14':
      daysToAdd = 14;
      break;
    case 'net_30':
      daysToAdd = 30;
      break;
    default:
      daysToAdd = 14;
  }

  const dueDate = new Date(now);
  dueDate.setDate(dueDate.getDate() + daysToAdd);
  return Timestamp.fromDate(dueDate);
};

export const invoiceService = {
  /**
   * Generate next invoice number (e.g., INV-2026-001)
   * Uses transaction to ensure unique numbers
   */
  async generateInvoiceNumber() {
    const settingsRef = doc(db, ...SETTINGS_DOC.split('/'));

    return await runTransaction(db, async (transaction) => {
      const settingsDoc = await transaction.get(settingsRef);
      const settings = settingsDoc.exists() ? settingsDoc.data() : {};
      const invoiceSettings = settings.invoiceSettings || {};

      const prefix = invoiceSettings.invoicePrefix || 'INV';
      const currentNumber = invoiceSettings.nextInvoiceNumber || 1;
      const year = new Date().getFullYear();

      // Format: INV-2026-001
      const invoiceNumber = `${prefix}-${year}-${String(currentNumber).padStart(3, '0')}`;

      // Increment the counter
      transaction.update(settingsRef, {
        'invoiceSettings.nextInvoiceNumber': currentNumber + 1,
        updatedAt: serverTimestamp()
      });

      return invoiceNumber;
    });
  },

  /**
   * Get invoice settings (bank details, tax rate, etc.)
   */
  async getInvoiceSettings() {
    try {
      const settingsRef = doc(db, ...SETTINGS_DOC.split('/'));
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        return settingsDoc.data().invoiceSettings || {};
      }
      return {};
    } catch (error) {
      console.error('Error fetching invoice settings:', error);
      return {};
    }
  },

  /**
   * Create a new invoice
   * @param {Object} data - Invoice data
   * @returns {Object} Created invoice with ID
   */
  async create(data) {
    try {
      // Get invoice settings for defaults
      const invoiceSettings = await this.getInvoiceSettings();
      // Only apply GST if explicitly registered in settings (matches admin logic)
      const gstRegistered = invoiceSettings?.gstRegistered === true;
      const defaultTaxRate = gstRegistered ? (invoiceSettings?.gstRate || 10) : 0;
      const effectiveTaxRate = gstRegistered ? (data.taxRate ?? defaultTaxRate) : 0;
      const paymentTerms = data.paymentTerms || invoiceSettings?.defaultPaymentTerms || 'net_14';

      // Generate invoice number if not provided
      const invoiceNumber = data.invoiceNumber || await this.generateInvoiceNumber();

      // Prepare line items with IDs
      const lineItems = (data.lineItems || []).map(item => ({
        id: item.id || generateId(),
        type: item.type || 'custom',
        referenceId: item.referenceId || null,
        description: item.description || '',
        quantity: item.quantity || 1,
        unitPrice: item.unitPrice || 0,
        total: item.total || (item.quantity || 1) * (item.unitPrice || 0),
        taxable: item.taxable !== false
      }));

      // Calculate totals with GST-aware tax rate
      const { subtotal, discountAmount, taxAmount, total } = calculateTotals(
        lineItems,
        data.discount,
        effectiveTaxRate
      );

      const invoiceData = {
        invoiceNumber,

        // Client info (denormalized)
        clientId: data.clientId || null,
        clientName: data.clientName || '',
        clientEmail: (data.clientEmail || '').toLowerCase(),
        clientPhone: data.clientPhone || '',
        clientAddress: data.clientAddress || '',

        // Line items
        lineItems,

        // Financial summary
        subtotal,
        discount: data.discount ? {
          type: data.discount.type || 'fixed',
          value: data.discount.value || 0,
          amount: discountAmount
        } : null,
        taxRate: effectiveTaxRate,
        gstRegistered,
        taxAmount,
        total,

        // Payment tracking
        payments: [],
        amountPaid: 0,
        amountDue: total,

        // Status
        status: data.status || 'draft', // Default to 'draft' - sent when booking is confirmed
        sentAt: null,
        viewedAt: null,
        paidAt: null,

        // Dates
        issueDate: data.issueDate || Timestamp.now(),
        dueDate: data.dueDate || calculateDueDate(paymentTerms),
        paymentTerms,

        // Source tracking (for linking to bookings)
        sourceType: data.sourceType || 'booking',
        sourceId: data.sourceId || null,

        // Notes
        notes: data.notes || '',
        publicNotes: data.publicNotes || '',
        termsAndConditions: data.termsAndConditions || '',

        // Email tracking
        emailHistory: [],
        reminderCount: 0,
        lastReminderSent: null,

        // Metadata
        createdBy: null, // No auth user on public site
        createdByEmail: 'website_booking',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, COLLECTION), invoiceData);

      // Log security event (optional, for audit trail)
      try {
        await addDoc(collection(db, 'securityLogs'), {
          action: 'create',
          collection: COLLECTION,
          documentId: docRef.id,
          userId: null,
          userEmail: 'website_booking',
          before: null,
          after: {
            invoiceNumber: invoiceData.invoiceNumber,
            clientName: invoiceData.clientName,
            total: invoiceData.total,
            status: invoiceData.status
          },
          changedFields: null,
          ip: null,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
          timestamp: serverTimestamp()
        });
      } catch (logError) {
        console.error('Error logging security event:', logError);
        // Don't throw - invoice was created successfully
      }

      return {
        id: docRef.id,
        ...invoiceData,
        // Convert timestamps for immediate use
        issueDate: invoiceData.issueDate,
        dueDate: invoiceData.dueDate
      };
    } catch (error) {
      console.error('Error creating invoice:', error);
      throw error;
    }
  },

  /**
   * Mark invoice as sent
   * @param {string} id - Invoice ID
   * @param {string} emailTo - Email address sent to
   */
  async markAsSent(id, emailTo) {
    try {
      const docRef = doc(db, COLLECTION, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Invoice not found');
      }

      const invoice = docSnap.data();
      const emailEntry = {
        type: 'invoice',
        sentAt: new Date().toISOString(),
        sentTo: emailTo,
        sentBy: 'system'
      };

      await updateDoc(docRef, {
        status: 'sent',
        sentAt: serverTimestamp(),
        emailHistory: [...(invoice.emailHistory || []), emailEntry],
        updatedAt: serverTimestamp()
      });

      return { id, ...invoice, status: 'sent' };
    } catch (error) {
      console.error('Error marking invoice as sent:', error);
      throw error;
    }
  },

  // Helper functions exported for use elsewhere
  calculateTotals,
  calculateDueDate,
  generateId
};

export default invoiceService;
